import React, { useState, useMemo } from "react";
import {
  useDestinations,
  useTenantDestinationAnalytics,
} from "../../hooks/use-destinations";
import { DestinationForm } from "../../components/destinations/destination-form";
import { DestinationCard } from "../../components/destinations/destination-card";
import { DestinationAnalytics } from "../../components/destinations/destination-analytics";
import type {
  DestinationFormData,
  DestinationModalState,
  DestinationSearchState,
} from "../../types/destination-types";
import {
  DEFAULT_DESTINATION_SEARCH_STATE,
  DESTINATION_CATEGORIES,
  getCategoryConfig,
} from "../../types/destination-types";
import type {
  DestinationEntity,
  CreateDestinationInput,
  UpdateDestinationInput,
} from "../../types/destination-core-types";

export function DestinationsPage() {
  const [searchState, setSearchState] = useState<DestinationSearchState>(
    DEFAULT_DESTINATION_SEARCH_STATE
  );
  const [modalState, setModalState] = useState<DestinationModalState>({
    isOpen: false,
    mode: "create",
    destination: null,
  });
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingDestination, setEditingDestination] =
    useState<DestinationEntity | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deletingDestinations, setDeletingDestinations] = useState<Set<string>>(
    new Set()
  );

  // Build filter for GraphQL query
  const filter = useMemo(() => {
    const graphqlFilter: any = {};

    if (searchState.query.trim()) {
      graphqlFilter.search = searchState.query.trim();
    }

    if (searchState.category !== "ALL") {
      graphqlFilter.category = searchState.category;
    }

    if (searchState.status !== "ALL") {
      graphqlFilter.isActive = searchState.status;
    }

    if (searchState.tags.length > 0) {
      graphqlFilter.tags = searchState.tags;
    }

    return Object.keys(graphqlFilter).length > 0 ? graphqlFilter : undefined;
  }, [searchState]);

  // Fetch destinations and analytics
  const {
    destinations,
    loading: destinationsLoading,
    createLoading,
    updateLoading,
    deleteLoading,
    error: destinationsError,
    total,
    hasMore,
    refetch,
    loadMore,
    createDestination,
    updateDestination,
    deleteDestination,
  } = useDestinations({ filter, limit: 25 });

  const { analytics: tenantAnalytics, loading: analyticsLoading } =
    useTenantDestinationAnalytics();

  // Filter destinations locally for immediate feedback
  const filteredDestinations = useMemo(() => {
    if (!searchState.query.trim()) return destinations;

    const searchText = searchState.query.toLowerCase();
    return destinations.filter(
      (destination) =>
        destination.name.toLowerCase().includes(searchText) ||
        destination.category.toLowerCase().includes(searchText) ||
        destination.url.toLowerCase().includes(searchText) ||
        destination.tags.some((tag) => tag.toLowerCase().includes(searchText))
    );
  }, [destinations, searchState.query]);
  // Event handlers
  const handleCreateDestination = async (data: DestinationFormData) => {
    if (isSubmitting) {
      return;
    }

    setIsSubmitting(true);
    try {
      const input: CreateDestinationInput = {
        name: data.name,
        url: data.url,
        category: data.category,
        description: data.description || undefined,
        tags: data.tags.length > 0 ? data.tags : undefined,
        trackingEnabled: data.trackingEnabled,
      };

      await createDestination(input);
      setShowCreateForm(false);
    } catch (error) {
      console.error("Failed to create destination:", error);
      throw error;
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateDestination = async (data: DestinationFormData) => {
    if (!editingDestination || isSubmitting) return;

    setIsSubmitting(true);
    try {
      const input: UpdateDestinationInput = {
        id: editingDestination.id,
        name: data.name,
        url: data.url,
        category: data.category,
        description: data.description || undefined,
        tags: data.tags.length > 0 ? data.tags : undefined,
        trackingEnabled: data.trackingEnabled,
      };

      await updateDestination(input);
      setEditingDestination(null);
      setShowCreateForm(false);
    } catch (error) {
      console.error("Failed to update destination:", error);
      throw error;
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteDestination = async (destination: DestinationEntity) => {
    if (!confirm(`Are you sure you want to delete "${destination.name}"?`)) {
      return;
    }

    // Prevent multiple delete operations on the same destination
    if (deletingDestinations.has(destination.id)) {
      return;
    }

    // Mark this destination as being deleted
    setDeletingDestinations((prev) => new Set(prev).add(destination.id));

    try {
      await deleteDestination(destination.id);
    } catch (error) {
      console.error("Failed to delete destination:", error);
      alert("Failed to delete destination. Please try again.");
    } finally {
      // Remove from deleting set regardless of success/failure
      setDeletingDestinations((prev) => {
        const newSet = new Set(prev);
        newSet.delete(destination.id);
        return newSet;
      });
    }
  };

  const handleEditDestination = (destination: DestinationEntity) => {
    setEditingDestination(destination);
    setShowCreateForm(true);
  };

  const handleViewDestination = (destination: DestinationEntity) => {
    setModalState({
      isOpen: true,
      mode: "view",
      destination,
    });
  };

  const formatNumber = (num: number): string => {
    if (num >= 1000000) {
      return `${(num / 1000000).toFixed(1)}M`;
    }
    if (num >= 1000) {
      return `${(num / 1000).toFixed(1)}K`;
    }
    return num.toString();
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="border-b border-gray-200 pb-4">
        <h1 className="text-3xl font-bold text-gray-900">Destinations</h1>
        <p className="mt-2 text-gray-600">
          Reusable URL endpoints for routing users to specific destinations
          across conversations.
        </p>
      </div>

      {/* Quick Stats */}
      {/* <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white p-6 rounded-lg shadow border">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center">
                <svg
                  className="w-5 h-5 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                  />
                </svg>
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">
                Active Destinations
              </p>
              <p className="text-2xl font-semibold text-gray-900">
                {analyticsLoading
                  ? "..."
                  : tenantAnalytics?.totalDestinations || total}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow border">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-green-500 rounded-lg flex items-center justify-center">
                <svg
                  className="w-5 h-5 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z"
                  />
                </svg>
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Total Clicks</p>
              <p className="text-2xl font-semibold text-gray-900">
                {analyticsLoading
                  ? "..."
                  : formatNumber(tenantAnalytics?.totalClicks || 0)}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow border">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-purple-500 rounded-lg flex items-center justify-center">
                <svg
                  className="w-5 h-5 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"
                  />
                </svg>
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">
                Conversion Rate
              </p>
              <p className="text-2xl font-semibold text-gray-900">
                {analyticsLoading
                  ? "..."
                  : tenantAnalytics?.averageConversionRate
                  ? `${tenantAnalytics.averageConversionRate.toFixed(1)}%`
                  : "N/A"}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow border">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-orange-500 rounded-lg flex items-center justify-center">
                <svg
                  className="w-5 h-5 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                  />
                </svg>
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Unique Clicks</p>
              <p className="text-2xl font-semibold text-gray-900">
                {analyticsLoading
                  ? "..."
                  : formatNumber(tenantAnalytics?.uniqueClicks || 0)}
              </p>
            </div>
          </div>
        </div>
      </div> */}

      {/* Create/Edit Destination */}
      {showCreateForm ? (
        <div className="bg-white rounded-lg shadow border">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">
              {editingDestination
                ? "Edit Destination"
                : "Create New Destination"}
            </h2>
          </div>
          <div className="p-6">
            <DestinationForm
              destination={editingDestination}
              mode={editingDestination ? "edit" : "create"}
              onSubmit={
                editingDestination
                  ? handleUpdateDestination
                  : handleCreateDestination
              }
              onCancel={() => {
                setShowCreateForm(false);
                setEditingDestination(null);
              }}
              loading={createLoading || updateLoading || isSubmitting}
            />
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow border p-6">
          <div className="text-center">
            <div className="w-12 h-12 bg-blue-100 rounded-lg mx-auto mb-4 flex items-center justify-center">
              <svg
                className="w-6 h-6 text-blue-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                />
              </svg>
            </div>
            <p className="text-gray-500 mb-4">
              Set up reusable URL endpoints that can be attached to multiple
              conversations.
            </p>
            <button
              onClick={() => setShowCreateForm(true)}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Create Destination
            </button>
          </div>
        </div>
      )}

      {/* Destination Categories */}
      {/* <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {DESTINATION_CATEGORIES.slice(0, 3).map((category) => {
          const categoryCount = destinations.filter(
            (d) => d.category === category.value
          ).length;

          return (
            <div
              key={category.value}
              className="bg-white rounded-lg shadow border p-6"
            >
              <div className="flex items-center mb-4">
                <div
                  className={`w-10 h-10 ${category.bgColor} rounded-lg flex items-center justify-center mr-3`}
                >
                  <svg
                    className={`w-5 h-5 ${category.textColor}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                    />
                  </svg>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">
                    {category.label}
                  </h3>
                  <p className="text-sm text-gray-500">
                    {categoryCount} destination{categoryCount !== 1 ? "s" : ""}
                  </p>
                </div>
              </div>
              <p className="text-gray-600 text-sm">{category.description}</p>
            </div>
          );
        })}
      </div> */}

      {/* Destinations List */}
      {destinations.length > 0 ? (
        <div className="bg-white rounded-lg shadow border">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <h2 className="text-lg font-semibold text-gray-900">
                  All Destinations ({total})
                </h2>
                {!showCreateForm && (
                  <button
                    onClick={() => setShowCreateForm(true)}
                    className="px-3 py-1 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    Add New
                  </button>
                )}
              </div>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg
                    className="h-4 w-4 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                    />
                  </svg>
                </div>
                <input
                  type="text"
                  placeholder="Search destinations..."
                  value={searchState.query}
                  onChange={(e) =>
                    setSearchState((prev) => ({
                      ...prev,
                      query: e.target.value,
                    }))
                  }
                  className="block w-64 pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                />
              </div>
            </div>
          </div>

          {/* Loading State */}
          {destinationsLoading && destinations.length === 0 ? (
            <div className="p-8">
              <div className="animate-pulse space-y-4">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-gray-200 rounded-lg"></div>
                    <div className="flex-1 space-y-2">
                      <div className="h-4 bg-gray-200 rounded w-1/4"></div>
                      <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <>
              {/* Destinations Grid */}
              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {filteredDestinations.map((destination) => (
                    <DestinationCard
                      key={destination.id}
                      destination={destination}
                      onEdit={handleEditDestination}
                      onDelete={handleDeleteDestination}
                      onView={handleViewDestination}
                      showAnalytics={true}
                      deleting={deletingDestinations.has(destination.id)}
                    />
                  ))}
                </div>

                {/* Load More Button */}
                {hasMore && (
                  <div className="mt-6 text-center">
                    <button
                      onClick={loadMore}
                      disabled={destinationsLoading}
                      className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
                    >
                      {destinationsLoading ? "Loading..." : "Load More"}
                    </button>
                  </div>
                )}

                {/* No Results Message */}
                {filteredDestinations.length === 0 &&
                  searchState.query.trim() && (
                    <div className="text-center py-12">
                      <div className="w-16 h-16 bg-gray-100 rounded-lg mx-auto mb-4 flex items-center justify-center">
                        <svg
                          className="w-8 h-8 text-gray-400"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                          />
                        </svg>
                      </div>
                      <h3 className="text-lg font-medium text-gray-900 mb-2">
                        No destinations found
                      </h3>
                      <p className="text-gray-500 mb-4">
                        No destinations match your search for "
                        {searchState.query}"
                      </p>
                      <button
                        onClick={() =>
                          setSearchState((prev) => ({ ...prev, query: "" }))
                        }
                        className="text-blue-600 hover:text-blue-700 font-medium"
                      >
                        Clear search
                      </button>
                    </div>
                  )}
              </div>
            </>
          )}
        </div>
      ) : !destinationsLoading && !showCreateForm ? (
        <div className="bg-white rounded-lg shadow border p-12">
          <div className="text-center">
            <div className="w-16 h-16 bg-gray-100 rounded-lg mx-auto mb-4 flex items-center justify-center">
              <svg
                className="w-8 h-8 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              No destinations yet
            </h3>
            <p className="text-gray-500 mb-6">
              Create your first destination to start routing users to specific
              endpoints.
            </p>
            <button
              onClick={() => setShowCreateForm(true)}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Create First Destination
            </button>
          </div>
        </div>
      ) : null}

      {/* Error State */}
      {destinationsError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg
                className="h-5 w-5 text-red-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">
                Error loading destinations
              </h3>
              <p className="mt-1 text-sm text-red-700">
                {destinationsError.message}
              </p>
              <div className="mt-2">
                <button
                  onClick={() => refetch()}
                  className="text-sm text-red-800 hover:text-red-900 font-medium"
                >
                  Try again
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal for Edit/View */}
      {modalState.isOpen && modalState.destination && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-900">
                  {modalState.mode === "edit"
                    ? "Edit Destination"
                    : modalState.mode === "view"
                    ? "Destination Details"
                    : "Destination"}
                </h2>
                <button
                  onClick={() =>
                    setModalState({
                      isOpen: false,
                      mode: "create",
                      destination: null,
                    })
                  }
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg
                    className="w-6 h-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
            </div>
            <div className="p-6">
              {modalState.mode === "edit" ? (
                <DestinationForm
                  destination={modalState.destination}
                  mode="edit"
                  onSubmit={handleUpdateDestination}
                  onCancel={() =>
                    setModalState({
                      isOpen: false,
                      mode: "create",
                      destination: null,
                    })
                  }
                  loading={updateLoading || isSubmitting}
                />
              ) : (
                <div className="space-y-6">
                  <DestinationCard
                    destination={modalState.destination}
                    showAnalytics={true}
                    compact={false}
                  />
                  <DestinationAnalytics
                    destinationId={modalState.destination.id}
                    showDetails={true}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
